<template>
    <div
        class="relative pb-xs md:bg-transparent">
        <!-- Tags -->
        <div class="absolute left-md top-md z-10 flex flex-col items-start space-y-xs">
            <client-only>
                <!-- Promotional tag -->
                <v-page-product-sale-tag v-if="promotion !== undefined && promotionTheme"
                    :promotion="promotion"
                    :price="price?.amount"
                    :original-price="originalPrice?.amount"
                    :promotion-price="promotionPrice?.amount"
                    :club-price="clubPrice" />

                <!-- Attribute tags -->
                <template v-if="attributesData">
                    <template v-for="attribute in attributesData"
                        :key="attribute.id">
                        <v-product-card-tag v-if="attribute.value"
                            :tag="attribute.value" />
                    </template>
                </template>
            </client-only>
        </div>

        <!-- Badges -->
        <div class="absolute right-md top-md z-10 flex flex-col items-end space-y-xs">
            <template v-for="badge in productBadges"
                :key="badge.id">
                <!-- Badge with link -->
                <nuxt-link v-if="badge.url && badge.icon"
                    :key="badge.title"
                    :to="badge.url"
                    tabindex="-1"
                    class="relative z-50">
                    <v-product-card-badge
                        :badge="badge">
                        {{ badge.title }}
                    </v-product-card-badge>
                </nuxt-link>

                <div v-if="badge.icon && !badge.url"
                    :key="badge.title"
                    tabindex="-1"
                    class="relative z-50">
                    <v-product-card-badge
                        :badge="badge">
                        {{ badge.title }}
                    </v-product-card-badge>
                </div>
            </template>
        </div>

        <!-- Gallery -->
        <!-- The md:grid classes turn the scrollable into a non-scrollable grid on big screens -->
        <div ref="galleryContainer"
            class="relative">
            <v-scrollable class="flex h-full snap-x snap-mandatory flex-nowrap md:grid
                md:grid-cols-2 md:gap-xs"
                no-scrollbar>
                <v-scrollable-item v-for="(media, index) in mediaItems"
                    :key="media.id"
                    :class="{
                        'md:col-span-2': index === 0,
                    }">
                    <!-- Regular image -->
                    <!-- eslint-disable tailwindcss/no-custom-classname -->
                    <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
                    <a v-if="media.__typename === 'ProductImage'"
                        :data-scroll-index="index"
                        :href="getImageUrl(media.url)"
                        :title="tg('accessibility.open-image', {
                            asset: media.title || activeProduct?.name || activeProduct?.title
                        })"
                        target="_blank"
                        rel="noreferrer"
                        class="product-image relative flex h-full w-screen shrink-0 snap-start
                            items-center justify-center md:w-auto md:overflow-hidden md:rounded"
                        :class="{
                            'px-md md:py-md': media.shotType === ProductMediaShotType.WhiteBackground,
                            '': media.shotType === ProductMediaShotType.Lifestyle,
                        }"
                        data-pswp-type="image"
                        :data-pswp-height="media.height ?? 600"
                        :data-pswp-width="media.width ?? 800"
                        :data-pswp-srcset="getImageSourceSet(media.url)"
                        tabindex="-1">
                        <div v-if="media.shotType === ProductMediaShotType.WhiteBackground"
                            class="absolute inset-0 bg-gray-500/5 group-hover:bg-gray-500/10 md:rounded"></div>

                        <nuxt-picture :src="media.url"
                            :alt="media.title || activeProduct?.title || activeProduct?.name"
                            :loading="index === 0 ? undefined : 'lazy'"
                            class="flex-1"
                            :preload="index === 0 ? true : false"
                            :sizes="
                                index === 0
                                    ? 'xs:90vw sm:80vw md:336px lg:464px xl:592px 2xl:720px'
                                    : 'xs:90vw sm:80vw md:140px lg:204px xl:268px 2xl:332px'
                            "
                            :height="media.height ?? undefined"
                            :width="media.width ?? undefined"
                            :img-attrs="getImageAttributes(media, index)" />

                        <!-- Brand logo / brand tag -->
                        <template v-if="index === 0">
                            <div class="absolute bottom-md left-md z-10">
                                <template v-if="brand">
                                    <v-page-product-brand-logo v-if="brand.logo"
                                        :key="brand.title"
                                        :brand="brand" />

                                    <template v-else>
                                        <v-product-card-tag v-if="!brand.logo && brand.title"
                                            :tag="brand.title" />
                                    </template>
                                </template>
                            </div>

                            <div class="absolute bottom-md right-md z-10">
                                <template v-for="badge in productBadges"
                                    :key="badge.title">
                                    <v-product-card-badge v-if="!badge.icon"
                                        :badge="badge">
                                        {{ badge.title }}
                                    </v-product-card-badge>
                                </template>
                            </div>
                        </template>
                    </a>
                    <!-- 360 image -->
                    <!-- eslint-disable tailwindcss/no-custom-classname -->
                    <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
                    <a v-else-if="media.__typename === 'ProductModel'"
                        :data-scroll-index="index"
                        :href="media.url"
                        :title="tg('accessibility.open-model', {
                            asset: media.title || activeProduct?.name || activeProduct?.title
                        })"
                        target="_blank"
                        rel="noreferrer"
                        class="product-image relative flex h-full w-screen shrink-0 snap-start
                            items-center justify-center md:w-auto md:overflow-hidden md:rounded"
                        data-pswp-type="model"
                        :data-model-url="media.url"
                        tabindex="-1">
                        <div class="flex size-full items-center justify-center
                            bg-gray-500/5 group-hover:bg-gray-500/10 md:rounded">
                            <v-icon class="text-[10rem] text-red-500 md:text-[6rem] lg:text-[8rem] xl:text-[10rem]"
                                icon="360"
                                :alt="`360 - ${media.title || activeProduct?.title || activeProduct?.name}`">
                            </v-icon>
                        </div>
                    </a>
                    <!-- Video -->
                    <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
                    <a v-else-if="media.__typename === 'ProductVideo'"
                        :data-scroll-index="index"
                        :href="getVideoPlayerUrl(media.url)"
                        :title="tg('accessibility.open-video', {
                            asset: media.title || activeProduct?.name || activeProduct?.title
                        })"
                        target="_blank"
                        rel="noreferrer"
                        class="product-image relative flex h-full max-h-[1200px] min-h-[150px] w-screen shrink-0
                            snap-start items-center justify-center
                            px-md md:w-auto
                            md:overflow-hidden md:rounded md:py-md"
                        data-pswp-type="video"
                        :data-video-url="getVideoPlayerUrl(media.url)"
                        :data-pswp-height="1200"
                        :data-pswp-width="1400"
                        :data-pswp-srcset="media.__typename === 'ProductVideo'
                            ? getVideoPlayerUrl(media.url)
                            : undefined"
                        tabindex="-1">

                        <nuxt-picture v-if="thumbnailsByMedia[media.id]"
                            :src="thumbnailsByMedia[media.id]"
                            :alt="media.title || activeProduct?.title || activeProduct?.name"
                            tabindex="-1"
                            :preload="{ fetchPriority: 'low' }"
                            :sizes="
                                index === 0
                                    ? 'xs:90vw sm:80vw md:336px lg:464px xl:592px 2xl:720px'
                                    : 'xs:90vw sm:80vw md:140px lg:204px xl:268px 2xl:332px'
                            "
                            :height="320"
                            :width="400"></nuxt-picture>

                        <div v-else>{{ tg('video') }}</div>
                    </a>
                </v-scrollable-item>
            </v-scrollable>

            <v-scrollable class="mt-xs flex flex-nowrap gap-xs px-sm py-xs sm:px-sm md:hidden"
                no-scrollbar>
                <v-scrollable-item v-for="(media, index) in mediaItems"
                    :key="media.id"
                    as="button"
                    type="button"
                    class="thumb relative block size-2xl shrink-0 overflow-hidden rounded-2xs outline-none
                        ring-black/20 transition duration-150 focus:ring sm:size-24"
                    :class="{
                        'ring-2': currentIndex === index,
                    }"
                    @click="scroll(index)">
                    <!-- Regular image -->
                    <template v-if="media.__typename === 'ProductImage'">
                        <div v-if="media.shotType === ProductMediaShotType.WhiteBackground"
                            class="absolute inset-0 bg-gray-500/5"></div>

                        <nuxt-picture :src="media.url"
                            :alt="media.title || activeProduct?.title || activeProduct?.name"
                            loading="lazy"
                            class="block"
                            :class="{
                                'm-xs': media.shotType === ProductMediaShotType.WhiteBackground
                            }"
                            sizes="xs:64px sm:96px md:96px lg:96px xl:96px 2xl:96px"
                            :height="media.height ?? undefined"
                            :width="media.width ?? undefined"
                            :img-attrs="getImageAttributes(media, index)" />
                    </template>

                    <!-- 360 image -->
                    <div v-else-if="media.__typename === 'ProductModel'"
                        class="flex size-full items-center justify-center bg-gray-500/5">
                        <v-icon class="text-[10rem] text-red-500"
                            icon="360"
                            :alt="`360 - ${media.title || activeProduct?.title || activeProduct?.name}`">
                        </v-icon>
                    </div>

                    <!-- Video -->
                    <nuxt-picture v-else-if="media.__typename === 'ProductVideo' && thumbnailsByMedia[media.id]"
                        :src="thumbnailsByMedia[media.id]"
                        :alt="media.title || activeProduct?.title || activeProduct?.name"
                        :preload="{ fetchPriority: 'low' }"
                        sizes="xs:64px sm:96px md:96px lg:96px xl:96px 2xl:96px"
                        :height="320"
                        :width="400" />
                </v-scrollable-item>
            </v-scrollable>
        </div>
    </div>
</template>

<script setup lang="ts">
import PhotoSwipe from 'photoswipe';
import {
    type Money,
    ProductMediaShotType,
    type SimpleProductDataFragment,
    type ViewProductDetailsQuery,
    type ProductAttributeDataFragment,
    ProductMediaOrientation,
} from '~~/graphql';

type DetailProduct = Extract<
    ViewProductDetailsQuery['product'],
    { __typename: 'SimpleProduct' }
>;

type ProductAttributeValue = ProductAttributeDataFragment;
type Promotion = DetailProduct['promotions'][0];
type Media = SimpleProductDataFragment['medias'][0];
type ProductBadge = DetailProduct['productBadges'];

interface AttributeData {
    id: string
    title: string
    value?: string
}

const properties = defineProps<{
    medias: Media[]
    price: Money | null
    originalPrice: Money | null
    promotionPrice: Money | null
    promotion?: Promotion | undefined
    activeProduct: SimpleProductDataFragment | undefined
    attributes: ProductAttributeValue[]
    productBadges: ProductBadge
}>();

const { t: tg } = useI18n({ useScope: 'global' });

const galleryContainer = ref<HTMLDivElement>();

const {
    clubPrice,
} = useClubPromotion(computed(() => properties.activeProduct), 'product');

const promotionTheme = computed(() => {
    if (properties.promotion?.__typename === 'CatalogPromotionPercent'
        || properties.promotion?.__typename === 'CatalogPromotionFixedAmount'
        || properties.promotion?.__typename === 'CatalogPromotionProductPromotionPrice') {
        const { style } = properties.promotion;

        return {
            mainTheme: style?.mainTheme,
            altTheme: style?.altTheme,
        };
    }
    return undefined;
});

const attributesData = computed<AttributeData[]>(() => (
    properties.attributes.map((attributeValue) => useAttributeValue(attributeValue))
));

const brand = computed(() => {
    if (!properties.activeProduct) {
        return undefined;
    }
    // We use this field to check whether the product has brand data attached to it
    if (!properties.activeProduct.brandLogo) {
        return undefined;
    }
    return {
        logo: properties.activeProduct.brandLogo,
        title: properties.activeProduct.brandTitle ?? undefined,
    };
});

const mediaItems = computed(() => {
    const medias = [...properties.medias]
        .sort((a, b) => {
            if (a.__typename === 'ProductImage' && a.isDefault) {
                return -1;
            }

            if (b.__typename === 'ProductImage' && b.isDefault) {
                return 1;
            }

            return a.position - b.position;
        });

    return medias;
});

function getImageAttributes(media: Media, index: number): Record<string, string> {
    const classNames: string[] = [
        'w-full',
        'object-contain',
        'aspect-square',
    ];

    if ((media.__typename === 'ProductImage' && media.orientation === ProductMediaOrientation.Portrait)
        && index === 0) {
        classNames.push('md:aspect-square h-full');
    } else {
        classNames.push('object-contain h-auto md:aspect-[4/3]');
    }

    if (media.__typename === 'ProductImage' && media.shotType === ProductMediaShotType.Lifestyle) {
        classNames.push('object-cover');
    } else {
        classNames.push('object-contain');
    }

    return {
        class: classNames.join(' '),
    };
}

// Non-dynamic semi-hack to use smaller paddings on mobile since the mobile version doesn't display back/next buttons so there's no need to have much horiz padding.
const lightboxXPadding = import.meta.client && window.innerWidth < 768 ? 20 : 80;

const lightBoxParameters = computed(() => ({
    gallery: galleryContainer.value,
    // Lightbox turns elements that match this query selector into gallery elements, makes them clickable and fetches their display info from their data attributes.
    children: 'a.product-image',
    pswpModule: PhotoSwipe,
    showHideAnimationType: 'fade',
    bgOpacity: 1,
    padding: {
        top: 80,
        right: lightboxXPadding,
        bottom: 80,
        left: lightboxXPadding,
    },
}));
const lightBoxParametersTyped = lightBoxParameters as Parameters<typeof useProductGallery>[0]['lightBoxParameters'];

const {
    thumbnailsByMedia,
    getVideoPlayerUrl,
    getImageUrl,
    getImageSourceSet,
} = useProductGallery({ lightBoxParameters: lightBoxParametersTyped, medias: mediaItems });

const { currentIndex, scroll } = useControlledScrollable(galleryContainer);

watch(currentIndex, (index) => {
    if (!galleryContainer.value) {
        return;
    }

    const child = galleryContainer.value.querySelector(`button.thumb:nth-child(${index + 1})`);

    if (!child) {
        return;
    }

    child.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'center',
    });
});
</script>

<style lang="scss">
.pswp {
    --pswp-bg: #fff;
    --pswp-icon-color: #EF1C35;
    --pswp-icon-color-secondary: #fff;
    --pswp-icon-stroke-width: 2px;
    --pswp-icon-stroke-color: #EF1C35;

    &__counter {
        font-size: 18px;
        font-weight: bold;
    }

    &__iframe-container {
        position: relative;
        width: 100%;
        height: 100%;
        pointer-events: none;
    }

    &__iframe-container iframe {
        width: 100%;
        height: 100%;
        max-width: 1200px;
        max-height: 1900px;
        pointer-events: auto;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
}
</style>

<i18n lang="json">
{
    "et": {
        "load-more-images": "Näita rohkem pilte"
    },
    "fi": {
        "load-more-images": "Lataa lisää kuvia"
    }
}
</i18n>
